/**
 * Vincent 2026-06-03 (Eric — item 26) — Page Sauvegardes & Restauration.
 *
 * - Bouton "Créer une sauvegarde maintenant" (manuel)
 * - Liste des sauvegardes existantes (auto + manuelles)
 * - Pour chaque ligne : 📥 Télécharger · 🔄 Restaurer (2 confirmations rouges) · 🗑 Supprimer
 *
 * Sécurité : la restauration EFFACE puis ré-insère les collections de l'org.
 * Avant la restauration, un snapshot de secours est créé automatiquement par
 * le backend pour rollback manuel si besoin.
 */
import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { confirmDialog } from "../lib/uiDialogs";
import {
  CloudArrowUp, CloudArrowDown, ArrowsClockwise, Trash, SpinnerGap, Warning,
  Database, ShieldCheck, Clock, Robot,
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import OffsiteSetupCard from "../components/admin/OffsiteSetupCard"; // Tâche 1251
import { useLang } from "../contexts/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL;

export default function BackupsPage() {
  const { tr } = useLang();
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [confirmRestore, setConfirmRestore] = useState(null); // { id, step: 1|2 }
  const [restoring, setRestoring] = useState(false);
  // Vincent #234 (Eric 2026-06-17 SPEC STRICK) — Onglet courant + schedule
  const [activeKind, setActiveKind] = useState("data");
  const [schedule, setSchedule] = useState({ enabled: true, interval_hours: 24, kinds: ["data"] });
  const [savingSchedule, setSavingSchedule] = useState(false);
  // Tâche 1245 (Eric 2026-06-28) — Confirmer que la copie hors site EXISTE.
  const [ovhCheck, setOvhCheck] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [retrying, setRetrying] = useState(null);

  // Tâche 1247 — Sans clic manuel, le bandeau montre le résultat de la
  // vérification NOCTURNE, déjà écrit sur chaque archive.
  const nightly = useMemo(() => {
    const verified = backups.filter((b) => b.ovh_verified);
    if (!verified.length) return null;
    const count = (state) => verified.filter((b) => b.ovh_verified === state).length;
    return {
      ovh_configured: true,
      checked: verified.length,
      confirmed: count("confirmed"),
      missing: count("missing"),
      mismatched: count("mismatched"),
      never_uploaded: backups.filter((b) => b.ovh_status !== "uploaded").length,
      verified_at: verified
        .map((b) => b.ovh_verified_at)
        .filter(Boolean)
        .sort()
        .slice(-1)[0],
      nightly: true,
    };
  }, [backups]);
  const ovhState = ovhCheck || nightly;

  const load = async () => {
    setLoading(true);
    try {
      const [bRes, sRes] = await Promise.all([
        axios.get(`${API}/api/admin/backups`, { withCredentials: true }),
        axios.get(`${API}/api/admin/backups/schedule`, { withCredentials: true }).catch(() => null),
      ]);
      setBackups(bRes.data || []);
      if (sRes?.data) setSchedule(sRes.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || tr("Erreur de chargement", "Loading failed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const saveSchedule = async (newSched) => {
    setSavingSchedule(true);
    try {
      const { data } = await axios.put(`${API}/api/admin/backups/schedule`, newSched, { withCredentials: true });
      setSchedule(data.schedule || newSched);
      toast.success(tr("Planification mise à jour", "Schedule updated"));
    } catch (e) {
      toast.error(e.response?.data?.detail || tr("Erreur de sauvegarde", "Save failed"));
    } finally {
      setSavingSchedule(false);
    }
  };

  const createBackup = async (kind = activeKind) => {
    setCreating(true);
    try {
      await axios.post(`${API}/api/admin/backups?backup_kind=${kind}`, {}, { withCredentials: true });
      toast.success(`Sauvegarde ${kind === "system" ? "système" : "données"} créée avec succès`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || tr("Erreur de création", "Creation failed"));
    } finally {
      setCreating(false);
    }
  };

  // Tâche 1245 — Jusqu'ici la colonne « OVH BHS » affichait le statut
  // enregistré au MOMENT de l'envoi : personne n'avait jamais vérifié que le
  // fichier était toujours dans le bucket. Une sauvegarde dont on ne vérifie
  // jamais la présence n'est pas une sauvegarde.
  const verifyOvh = async () => {
    setVerifying(true);
    try {
      const { data } = await axios.post(`${API}/api/admin/backups/verify-ovh`, {}, {
        withCredentials: true, timeout: 120000,
      });
      setOvhCheck(data);
      if (!data.ovh_configured) {
        toast.warning(tr("Aucune copie hors site : OVH n'est pas configuré pour cette organisation", "No off-site copy: OVH is not configured for this organisation"));
      } else if (data.missing || data.mismatched) {
        toast.error(`${data.missing} copie(s) manquante(s), ${data.mismatched} de taille différente`);
      } else {
        toast.success(`${data.confirmed} copie(s) confirmée(s) chez OVH`);
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || tr("Vérification impossible", "Verification failed"));
    } finally {
      setVerifying(false);
    }
  };

  const retryOvh = async (bk) => {
    setRetrying(bk.id);
    try {
      const { data } = await axios.post(`${API}/api/admin/backups/${bk.id}/ovh-retry`, {}, {
        withCredentials: true, timeout: 180000,
      });
      setOvhCheck(data.verification);
      toast.success(tr("Archive renvoyée chez OVH et vérifiée", "Archive sent back to OVH and verified"));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Renvoi impossible");
    } finally {
      setRetrying(null);
    }
  };

  const downloadBackup = (bk) => {
    const url = `${API}/api/admin/backups/${bk.id}/download`;
    const a = document.createElement("a");
    a.href = url;
    a.download = bk.filename || `${bk.id}.json.gz`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const deleteBackup = async (bk) => {
    const ok = await confirmDialog(`Supprimer définitivement la sauvegarde ${bk.id} ?\n\nCette action est irréversible.`);
    if (!ok) return;
    try {
      await axios.delete(`${API}/api/admin/backups/${bk.id}`, { withCredentials: true });
      toast.success(tr("Sauvegarde supprimée", "Backup deleted"));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || tr("Erreur de suppression", "Deletion failed"));
    }
  };

  const startRestore = (bk) => setConfirmRestore({ ...bk, step: 1 });

  const performRestore = async () => {
    if (!confirmRestore) return;
    setRestoring(true);
    try {
      const { data } = await axios.post(
        `${API}/api/admin/backups/${confirmRestore.id}/restore`, {},
        { withCredentials: true, timeout: 120000 }
      );
      toast.success(`Restauration terminée. Snapshot de secours : ${data.safety_snapshot_id}`);
      setConfirmRestore(null);
      load();
      // Eric — recharger toute la session car des collections critiques ont changé
      setTimeout(() => { window.location.reload(); }, 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || tr("Erreur lors de la restauration", "Restore failed"));
      setRestoring(false);
    }
  };

  const formatSize = (b) => {
    if (!b) return "—";
    if (b < 1024) return `${b} o`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} Ko`;
    return `${(b / 1024 / 1024).toFixed(2)} Mo`;
  };

  const formatDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleString("fr-CA", { dateStyle: "medium", timeStyle: "short" });
  };

  return (
    <div className="container mx-auto py-8 px-4 max-w-5xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold flex items-center gap-3 mb-2">
          <Database size={32} className="text-blue-600" weight="duotone" />
          {tr("Sauvegardes & Restauration", "Backups & Restore")}
        </h1>
        <p className="text-sm text-zinc-600 max-w-2xl">
          {tr("Système complet de protection de vos données. Les sauvegardes automatiques sont créées quotidiennement.", "Full protection for your data. Automatic backups run every day.")}
          {" "}{tr("Vous pouvez aussi créer une sauvegarde manuelle à tout moment, télécharger les archives ou restaurer une sauvegarde précédente.", "You can also create a backup by hand at any time, download the archives, or restore an earlier backup.")}
        </p>
      </div>

      {/* Cartes infos */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="border border-blue-200 bg-blue-50 rounded-lg p-4">
          <div className="flex items-center gap-2 text-blue-800 font-semibold mb-1">
            <Robot size={18} weight="duotone" /> {tr("Automatique", "Automatic")}
          </div>
          <p className="text-xs text-blue-700">{tr("Sauvegarde quotidienne au démarrage du serveur. Aucune action requise.", "Daily backup when the server starts. Nothing to do.")}</p>
        </div>
        <div className="border border-teal-200 bg-teal-50 rounded-lg p-4">
          <div className="flex items-center gap-2 text-teal-800 font-semibold mb-1">
            <ShieldCheck size={18} weight="duotone" /> {tr("Sécurité", "Security")}
          </div>
          <p className="text-xs text-teal-700">{tr("Isolation tenant stricte. Une sauvegarde ne peut être restaurée que dans la même organisation.", "Strict tenant isolation. A backup can only be restored into the same organisation.")}</p>
        </div>
        <div className="border border-amber-200 bg-amber-50 rounded-lg p-4">
          <div className="flex items-center gap-2 text-amber-800 font-semibold mb-1">
            <Clock size={18} weight="duotone" /> {tr("Sauvegarde de secours", "Safety backup")}
          </div>
          <p className="text-xs text-amber-700">{tr("Avant chaque restauration, l'état actuel est automatiquement sauvegardé pour rollback.", "Before every restore, the current state is backed up automatically so you can roll back.")}</p>
        </div>
      </div>

      <div className="mb-4 flex items-center justify-between gap-3 flex-wrap">
        {/* Vincent #234 — Tabs : Données vs Système */}
        <div className="flex gap-1 p-1 bg-zinc-100 rounded-lg" data-testid="backups-tabs">
          <button
            onClick={() => setActiveKind("data")}
            className={`px-4 py-2 text-sm rounded-md font-medium transition-colors ${
              activeKind === "data" ? "bg-white text-blue-700 shadow-sm" : "text-zinc-600 hover:text-zinc-900"
            }`}
            data-testid="tab-backups-data"
          >
            {tr("Données", "Data")} ({backups.filter(b => (b.backup_kind || "data") !== "system").length})
          </button>
          <button
            onClick={() => setActiveKind("system")}
            className={`px-4 py-2 text-sm rounded-md font-medium transition-colors ${
              activeKind === "system" ? "bg-white text-blue-700 shadow-sm" : "text-zinc-600 hover:text-zinc-900"
            }`}
            data-testid="tab-backups-system"
          >
            {tr("Système", "System")} ({backups.filter(b => b.backup_kind === "system").length})
          </button>
        </div>
        <Button
          onClick={() => createBackup(activeKind)}
          disabled={creating}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white"
          data-testid="create-backup-btn"
        >
          {creating ? <SpinnerGap size={16} className="animate-spin" /> : <CloudArrowUp size={18} weight="bold" />}
          {creating
            ? tr("Création…", "Creating…")
            : tr(
                `Créer une sauvegarde ${activeKind === "system" ? "système" : "données"}`,
                `Create a ${activeKind === "system" ? "system" : "data"} backup`,
              )}
        </Button>
      </div>

      {/* Vincent #234 — Planification de sauvegarde auto */}
      <div className="mb-6 border border-blue-200 bg-blue-50 rounded-lg p-4" data-testid="backup-schedule-card">
        <div className="flex items-center gap-2 mb-2 text-blue-900 font-semibold">
          <Clock size={18} weight="duotone" /> {tr("Planification automatique", "Automatic schedule")}
        </div>
        <div className="flex items-center gap-4 flex-wrap text-sm">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={schedule.enabled}
              onChange={(e) => saveSchedule({ ...schedule, enabled: e.target.checked })}
              disabled={savingSchedule}
              className="w-4 h-4"
              data-testid="schedule-enabled-checkbox"
            />
            <span>{tr("Activée", "Enabled")}</span>
          </label>
          <label className="flex items-center gap-2">
            {tr("Intervalle :", "Interval:")}
            <select
              value={schedule.interval_hours}
              onChange={(e) => saveSchedule({ ...schedule, interval_hours: Number(e.target.value) })}
              disabled={savingSchedule || !schedule.enabled}
              className="px-2 py-1 border border-blue-300 rounded text-sm bg-white"
              data-testid="schedule-interval-select"
            >
              <option value={6}>{tr("Toutes les 6h", "Every 6 h")}</option>
              <option value={12}>{tr("Toutes les 12h", "Every 12 h")}</option>
              <option value={24}>{tr("Toutes les 24h (quotidien)", "Every 24 h (daily)")}</option>
              <option value={48}>{tr("Tous les 2 jours", "Every 2 days")}</option>
              <option value={168}>{tr("Hebdomadaire", "Weekly")}</option>
            </select>
          </label>
          <label className="flex items-center gap-2">
            {tr("Types :", "Types:")}
            <span className="inline-flex gap-2">
              {["data", "system"].map((k) => (
                <button
                  key={k}
                  onClick={() => {
                    const newKinds = schedule.kinds.includes(k)
                      ? schedule.kinds.filter((x) => x !== k)
                      : [...schedule.kinds, k];
                    saveSchedule({ ...schedule, kinds: newKinds.length ? newKinds : ["data"] });
                  }}
                  disabled={savingSchedule || !schedule.enabled}
                  className={`px-2 py-1 rounded text-xs ${
                    schedule.kinds.includes(k)
                      ? "bg-blue-600 text-white"
                      : "bg-white border border-blue-300 text-blue-700"
                  }`}
                  data-testid={`schedule-kind-${k}`}
                >
                  {k === "data" ? "Données" : "Système"}
                </button>
              ))}
            </span>
          </label>
        </div>
      </div>

      {/* Tâche 1251 — Assistant : rien n'est « confirmé » avant d'avoir
          RETROUVÉ l'archive dans le bucket. */}
      {(!ovhState || !ovhState.ovh_configured || ovhState.missing > 0) && (
        <OffsiteSetupCard onDone={load} />
      )}

      {/* Tâche 1245 — Bandeau franc sur l'état de la copie hors site */}
      <div
        className={`mb-6 rounded-lg p-4 border ${
          ovhState && !ovhState.ovh_configured
            ? "border-red-300 bg-red-50"
            : ovhState && (ovhState.missing || ovhState.mismatched)
              ? "border-amber-300 bg-amber-50"
              : "border-zinc-200 bg-zinc-50"
        }`}
        data-testid="ovh-offsite-banner"
      >
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2 font-semibold text-zinc-900">
              <ShieldCheck size={18} weight="duotone" /> {tr("Copie hors site (OVH BHS)", "Off-site copy (OVH BHS)")}
            </div>
            <p className="text-xs text-zinc-700 mt-1 max-w-2xl" data-testid="ovh-offsite-verdict">
              {!ovhState ? (
                tr("La colonne « OVH BHS » montre le statut enregistré au moment de l'envoi. Lancez une vérification pour confirmer, bucket en main, que les archives sont toujours là.", "The “OVH BHS” column shows the status recorded at upload time. Run a verification to confirm, bucket in hand, that the archives are still there.")
              ) : !ovhState.ovh_configured ? (
                tr("Aucune copie hors site — une seule copie, sur le disque du serveur. Si ce disque est perdu, les données le sont aussi. Renseignez les clés OVH dans Forfait AI › Clés BYOK.", "No off-site copy — a single copy, on the server disk. Lose that disk and you lose the data. Enter the OVH keys under AI Package › BYOK keys.")
              ) : (
                `${ovhState.confirmed} archive(s) confirmée(s) sur ${ovhState.checked} vérifiée(s)` +
                (ovhState.missing ? ` · ${ovhState.missing} MANQUANTE(S) dans le bucket` : "") +
                (ovhState.mismatched ? ` · ${ovhState.mismatched} de taille différente` : "") +
                (ovhState.never_uploaded ? ` · ${ovhState.never_uploaded} jamais envoyée(s)` : "") +
                ` · ${ovhState.nightly ? "vérification nocturne du" : "vérifié le"} ${formatDate(ovhState.verified_at)}`
              )}
            </p>
          </div>
          <Button
            onClick={verifyOvh}
            disabled={verifying}
            variant="outline"
            className="flex items-center gap-2"
            data-testid="ovh-verify-all-btn"
          >
            {verifying ? <SpinnerGap size={16} className="animate-spin" /> : <ShieldCheck size={16} weight="bold" />}
            {verifying ? tr("Vérification…", "Verifying…") : tr("Tout vérifier", "Verify everything")}
          </Button>
        </div>
      </div>

      {/* Liste */}
      {loading ? (
        <div className="text-center py-12 text-zinc-500"><SpinnerGap size={32} className="animate-spin mx-auto mb-2" /> Chargement...</div>
      ) : backups.length === 0 ? (
        <div className="border border-zinc-200 rounded-lg p-12 text-center text-zinc-500">
          {tr("Aucune sauvegarde pour le moment. Cliquez \"Créer une sauvegarde maintenant\" pour démarrer.", "No backup yet. Click “Create a backup now” to start.")}
        </div>
      ) : (() => {
        const filtered = backups.filter((b) => activeKind === "system" ? b.backup_kind === "system" : (b.backup_kind || "data") !== "system");
        if (filtered.length === 0) {
          return (
            <div className="border border-zinc-200 rounded-lg p-12 text-center text-zinc-500" data-testid="backups-empty">
              Aucune sauvegarde {activeKind === "system" ? "système" : "données"} pour le moment.
            </div>
          );
        }
        // Tâche 1395 — `overflow-hidden` COUPAIT le tableau à 390 px : les
        // colonnes de droite (dont « Supprimer ») devenaient inatteignables
        // au doigt. On défile désormais horizontalement.
        return (
        <div className="border border-zinc-200 rounded-lg overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr>
                <th className="text-left px-4 py-2 font-semibold text-zinc-700">Mode</th>
                <th className="text-left px-4 py-2 font-semibold text-zinc-700">Date</th>
                <th className="text-right px-4 py-2 font-semibold text-zinc-700">Docs</th>
                <th className="text-right px-4 py-2 font-semibold text-zinc-700">Taille</th>
                <th className="text-center px-4 py-2 font-semibold text-zinc-700">OVH BHS</th>
                <th className="text-center px-4 py-2 font-semibold text-zinc-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((bk) => (
                <tr key={bk.id} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50" data-testid={`backup-row-${bk.id}`}>
                  <td className="px-4 py-2">
                    <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded ${
                      bk.mode === "auto" ? "bg-blue-100 text-blue-700" :
                      bk.mode === "pre_restore_safety" ? "bg-amber-100 text-amber-700" :
                      "bg-teal-100 text-teal-700"
                    }`}>
                      {bk.mode === "auto" ? "Auto" : bk.mode === "pre_restore_safety" ? "Pré-restore" : "Manuel"}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-zinc-600">{formatDate(bk.created_at)}</td>
                  <td className="px-4 py-2 text-right text-zinc-600 font-mono">{bk.total_docs}</td>
                  <td className="px-4 py-2 text-right text-zinc-600 font-mono">{formatSize(bk.size_bytes)}</td>
                  <td className="px-4 py-2 text-center text-xs" data-testid={`ovh-cell-${bk.id}`}>
                    {bk.ovh_verified === "confirmed" ? (
                      <span className="inline-flex items-center gap-1 text-teal-700" title={`Bucket : ${bk.ovh_bucket} · vérifié le ${formatDate(bk.ovh_verified_at)}`}>
                        {tr("🍁 Confirmé", "🍁 Confirmed")}
                      </span>
                    ) : bk.ovh_verified === "missing" ? (
                      <span className="text-red-600 font-semibold" title={bk.ovh_verify_note || tr("Objet absent du bucket", "Object missing from the bucket")}>
                        {tr("⚠️ Manquante", "⚠️ Missing")}
                      </span>
                    ) : bk.ovh_verified === "mismatched" ? (
                      <span className="text-amber-700 font-semibold" title={bk.ovh_verify_note || ""}>
                        {tr("⚠️ Taille différente", "⚠️ Size mismatch")}
                      </span>
                    ) : bk.ovh_status === "uploaded" ? (
                      <span className="inline-flex items-center gap-1 text-zinc-600" title={`Bucket : ${bk.ovh_bucket} — jamais vérifié depuis l'envoi`}>
                        {tr("🍁 Envoyé, non vérifié", "🍁 Uploaded, not verified")}
                      </span>
                    ) : bk.ovh_status === "error" ? (
                      <span className="text-red-600" title={bk.ovh_error || tr("Échec de l'envoi OVH — archive locale intacte", "OVH upload failed — the local archive is intact")}>{tr("⚠️ Erreur", "⚠️ Error")}</span>
                    ) : (
                      <span className="text-zinc-400" title={tr("Aucune copie hors site pour cette archive", "No off-site copy for this archive")}>{tr("Aucune copie", "No copy")}</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-center">
                    <div className="inline-flex items-center gap-1">
                      <button onClick={() => downloadBackup(bk)} className="p-1.5 hover:bg-blue-50 text-blue-600 rounded" title={tr("Télécharger", "Download")} data-testid={`download-${bk.id}`}>
                        <CloudArrowDown size={16} />
                      </button>
                      <button onClick={() => startRestore(bk)} className="p-1.5 hover:bg-amber-50 text-amber-700 rounded" title="Restaurer" data-testid={`restore-${bk.id}`}>
                        <ArrowsClockwise size={16} />
                      </button>
                      {(bk.ovh_verified === "missing" || bk.ovh_verified === "mismatched" ||
                        bk.ovh_status === "error" || !bk.ovh_status || bk.ovh_status === "skipped") && (
                        <button
                          onClick={() => retryOvh(bk)}
                          disabled={retrying === bk.id}
                          className="p-1.5 hover:bg-teal-50 text-teal-700 rounded disabled:opacity-50"
                          title={tr("Renvoyer chez OVH puis vérifier", "Send to OVH again, then verify")}
                          data-testid={`ovh-retry-${bk.id}`}
                        >
                          {retrying === bk.id
                            ? <SpinnerGap size={16} className="animate-spin" />
                            : <CloudArrowUp size={16} />}
                        </button>
                      )}
                      <button onClick={() => deleteBackup(bk)} className="p-1.5 hover:bg-red-50 text-red-600 rounded" title={tr("Supprimer", "Delete")} data-testid={`delete-${bk.id}`}>
                        <Trash size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        );
      })()}

      {/* Confirmation MODALE — 2 étapes rouges, comme Vincent l'a demandé */}
      {confirmRestore && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => !restoring && setConfirmRestore(null)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 border-4 border-red-500" onClick={(e) => e.stopPropagation()} data-testid="restore-confirm-modal">
            <div className="flex items-start gap-3 mb-4">
              <Warning size={32} className="text-red-600 shrink-0" weight="duotone" />
              <div>
                <h3 className="text-lg font-bold text-red-900">
                  {confirmRestore.step === 1 ? "⚠️ Confirmation 1 / 2" : "🚨 Confirmation FINALE 2 / 2"}
                </h3>
                <p className="text-sm text-red-700 mt-1">
                  {confirmRestore.step === 1
                    ? tr("Vous êtes sur le point de RESTAURER une ancienne sauvegarde. Toutes les données actuelles de votre organisation seront REMPLACÉES.", "You are about to RESTORE an older backup. Every piece of your organisation's current data will be REPLACED.")
                    : "DERNIÈRE CHANCE. Une fois validée, la restauration est IMMÉDIATE. Un snapshot de secours est créé automatiquement avant pour rollback manuel."}
                </p>
              </div>
            </div>
            <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-3 mb-4 text-xs text-zinc-700">
              <div><strong>{tr("Sauvegarde :", "Backup:")}</strong> {confirmRestore.id}</div>
              <div><strong>{tr("Date :", "Date:")}</strong> {formatDate(confirmRestore.created_at)}</div>
              <div><strong>{tr("Documents :", "Documents:")}</strong> {confirmRestore.total_docs}</div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setConfirmRestore(null)} disabled={restoring} data-testid="restore-cancel-btn">
                {tr("Annuler", "Cancel")}
              </Button>
              {confirmRestore.step === 1 ? (
                <Button
                  onClick={() => setConfirmRestore({ ...confirmRestore, step: 2 })}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="restore-confirm-1-btn"
                >
                  Continuer
                </Button>
              ) : (
                <Button
                  onClick={performRestore}
                  disabled={restoring}
                  className="bg-red-700 hover:bg-red-800 text-white"
                  data-testid="restore-confirm-2-btn"
                >
                  {restoring
                    ? <><SpinnerGap size={14} className="animate-spin mr-1" /> {tr("Restauration…", "Restoring…")}</>
                    : tr("RESTAURER MAINTENANT", "RESTORE NOW")}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
